<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <div class="bg-white shadow">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="flex items-center justify-between flex-wrap gap-3">
          <div class="flex items-center">
            <i class="fas fa-terminal text-indigo-600 text-3xl mr-3"></i>
            <div>
              <h1 class="text-2xl font-bold text-gray-900">SQL Query Explorer</h1>
              <p class="text-sm text-gray-500 mt-1">
                Exécution de requêtes <strong>SELECT</strong> sur MSSQL et MySQL
              </p>
            </div>
          </div>
          <div class="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            <i class="fas fa-shield-alt"></i>
            <span>Lecture seule — les requêtes INSERT / UPDATE / DELETE sont refusées</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

      <!-- Query editor card -->
      <div class="bg-white rounded-lg shadow overflow-hidden">
        <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
          <!-- Connection selector -->
          <div class="flex items-center gap-4">
            <span class="text-sm font-medium text-gray-700">Connexion :</span>
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                v-model="connection"
                value="mysql"
                class="text-indigo-600 focus:ring-indigo-500"
              />
              <span class="text-sm text-gray-700">
                <i class="fas fa-database text-blue-500 mr-1"></i>MySQL (nouvelle DB)
              </span>
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                v-model="connection"
                value="mssql"
                class="text-indigo-600 focus:ring-indigo-500"
              />
              <span class="text-sm text-gray-700">
                <i class="fas fa-database text-orange-500 mr-1"></i>MSSQL (ancienne DB)
              </span>
            </label>
          </div>

          <!-- History button -->
          <button
            v-if="history.length > 0"
            @click="showHistory = !showHistory"
            class="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
          >
            <i class="fas fa-history"></i>
            Historique ({{ history.length }})
          </button>
        </div>

        <!-- History dropdown -->
        <div v-if="showHistory && history.length > 0" class="border-b border-gray-200 bg-gray-50 px-6 py-3 max-h-56 overflow-y-auto">
          <p class="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Requêtes récentes</p>
          <div
            v-for="(item, idx) in history"
            :key="idx"
            class="flex items-start gap-2 py-1.5 border-b border-gray-100 last:border-0 group cursor-pointer hover:bg-white rounded px-1"
            @click="loadFromHistory(item)"
          >
            <span
              :class="item.connection === 'mssql' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'"
              class="text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 mt-0.5"
            >{{ item.connection.toUpperCase() }}</span>
            <span class="text-xs text-gray-700 truncate flex-1 font-mono">{{ item.query }}</span>
            <span class="text-xs text-gray-400 flex-shrink-0">{{ item.time }}</span>
          </div>
          <button
            @click="clearHistory"
            class="mt-2 text-xs text-red-500 hover:text-red-700"
          >
            <i class="fas fa-trash mr-1"></i>Effacer l'historique
          </button>
        </div>

        <!-- Textarea -->
        <div class="px-6 py-4">
          <textarea
            v-model="query"
            @keydown.ctrl.enter.prevent="executeQuery"
            @keydown.meta.enter.prevent="executeQuery"
            placeholder="SELECT * FROM property WHERE occupation = 'Vacant' LIMIT 100"
            rows="8"
            class="w-full font-mono text-sm border border-gray-300 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y placeholder-gray-400"
            :disabled="loading"
          ></textarea>
          <p class="text-xs text-gray-400 mt-1">
            Raccourci clavier : <kbd class="bg-gray-100 px-1.5 py-0.5 rounded border border-gray-300 font-mono">Ctrl</kbd> +
            <kbd class="bg-gray-100 px-1.5 py-0.5 rounded border border-gray-300 font-mono">Entrée</kbd>
          </p>
        </div>

        <!-- Actions -->
        <div class="px-6 pb-4 flex items-center gap-3">
          <button
            @click="executeQuery"
            :disabled="loading || !query.trim()"
            class="inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i :class="loading ? 'fas fa-spinner fa-spin' : 'fas fa-play'" class="mr-2"></i>
            {{ loading ? 'Exécution...' : 'Exécuter' }}
          </button>
          <button
            @click="clearQuery"
            :disabled="loading"
            class="inline-flex items-center px-4 py-2.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i class="fas fa-times mr-2"></i>
            Effacer
          </button>

          <!-- Quick queries -->
          <div class="ml-auto flex items-center gap-2 flex-wrap justify-end">
            <span class="text-xs text-gray-500">Requêtes rapides :</span>
            <button
              v-for="quick in quickQueries"
              :key="quick.label"
              @click="loadQuickQuery(quick)"
              class="text-xs px-2.5 py-1.5 border border-gray-200 rounded-md bg-gray-50 hover:bg-gray-100 text-gray-600 hover:text-gray-800 font-mono"
            >
              {{ quick.label }}
            </button>
          </div>
        </div>
      </div>

      <!-- Error message -->
      <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
        <i class="fas fa-exclamation-circle text-red-400 text-lg flex-shrink-0 mt-0.5"></i>
        <div>
          <h3 class="text-sm font-medium text-red-800">Erreur SQL</h3>
          <p class="mt-1 text-sm text-red-700 font-mono whitespace-pre-wrap">{{ error }}</p>
        </div>
      </div>

      <!-- Results -->
      <div v-if="result" class="bg-white rounded-lg shadow overflow-hidden">
        <!-- Results header -->
        <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
          <div class="flex items-center gap-3">
            <i class="fas fa-table text-green-500"></i>
            <h2 class="text-sm font-semibold text-gray-900">
              Résultats
            </h2>
            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              {{ result.rowCount.toLocaleString() }} ligne{{ result.rowCount > 1 ? 's' : '' }}
            </span>
            <span
              :class="result.connection === 'mssql' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'"
              class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            >
              <i class="fas fa-database mr-1"></i>
              {{ result.connection === 'mssql' ? 'MSSQL' : 'MySQL' }}
            </span>
            <span class="text-xs text-gray-400">
              <i class="fas fa-clock mr-1"></i>{{ result.executionTime }} ms
            </span>
          </div>
          <button
            @click="exportCsv"
            v-if="result.rows.length > 0"
            class="inline-flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900 border border-gray-200 rounded px-2.5 py-1.5 hover:bg-gray-50"
          >
            <i class="fas fa-download"></i>
            Export CSV
          </button>
        </div>

        <!-- Empty results -->
        <div v-if="result.rows.length === 0" class="px-6 py-8 text-center text-gray-500">
          <i class="fas fa-inbox text-3xl mb-2 block"></i>
          Aucun résultat retourné.
        </div>

        <!-- Results table -->
        <div v-else class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 text-sm">
            <thead class="bg-gray-50 sticky top-0">
              <tr>
                <th
                  v-for="col in result.columns"
                  :key="col"
                  class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                >
                  {{ col }}
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-100">
              <tr
                v-for="(row, rowIdx) in paginatedRows"
                :key="rowIdx"
                class="hover:bg-gray-50"
              >
                <td
                  v-for="col in result.columns"
                  :key="col"
                  class="px-4 py-2 text-gray-700 font-mono whitespace-nowrap max-w-xs truncate"
                  :title="row[col] !== null && row[col] !== undefined ? String(row[col]) : ''"
                >
                  <span v-if="row[col] === null" class="text-gray-300 italic text-xs">NULL</span>
                  <span v-else>{{ row[col] }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="result.rows.length > pageSize" class="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
          <span class="text-xs text-gray-500">
            Affichage {{ paginationStart + 1 }}–{{ paginationEnd }} sur {{ result.rows.length }}
          </span>
          <div class="flex items-center gap-2">
            <button
              @click="page--"
              :disabled="page <= 1"
              class="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <i class="fas fa-chevron-left"></i>
            </button>
            <span class="text-xs text-gray-700">Page {{ page }} / {{ totalPages }}</span>
            <button
              @click="page++"
              :disabled="page >= totalPages"
              class="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import api from '@/services/api'

const HISTORY_KEY = 'sql_query_history'
const MAX_HISTORY = 20
const PAGE_SIZE = 200

export default {
  name: 'SqlQuery',

  data() {
    return {
      connection: 'mysql',
      query: '',
      loading: false,
      error: null,
      result: null,
      history: [],
      showHistory: false,
      page: 1,
      pageSize: PAGE_SIZE,

      quickQueries: [
        {
          label: 'Tables MySQL',
          connection: 'mysql',
          query: 'SHOW TABLES'
        },
        {
          label: 'Tables MSSQL',
          connection: 'mssql',
          query: "SELECT TABLE_NAME FROM INFORMATION_SCHEMA.TABLES WHERE TABLE_TYPE = 'BASE TABLE' ORDER BY TABLE_NAME"
        },
        {
          label: 'Properties (MySQL)',
          connection: 'mysql',
          query: "SELECT p.id, p.reference, p.occupation, p.scheduled_visit_at, p.last_visit_at\nFROM property p\nLIMIT 50"
        },
        {
          label: 'Visites (MySQL)',
          connection: 'mysql',
          query: "SELECT v.id, v.visited_at, v.status, p.reference\nFROM visit v\nJOIN property p ON p.id = v.property_id\nORDER BY v.visited_at DESC\nLIMIT 50"
        },
        {
          label: 'Visites (MSSQL)',
          connection: 'mssql',
          query: "SELECT TOP 50 v.Id, v.VisitedAt, v.Status, p.Reference\nFROM Visit v\nJOIN Property p ON p.Id = v.PropertyId\nORDER BY v.VisitedAt DESC"
        },
      ],
    }
  },

  computed: {
    paginationStart() {
      return (this.page - 1) * this.pageSize
    },
    paginationEnd() {
      return Math.min(this.page * this.pageSize, this.result ? this.result.rows.length : 0)
    },
    totalPages() {
      return this.result ? Math.ceil(this.result.rows.length / this.pageSize) : 1
    },
    paginatedRows() {
      if (!this.result) return []
      return this.result.rows.slice(this.paginationStart, this.paginationEnd)
    },
  },

  mounted() {
    this.loadHistory()
  },

  methods: {
    async executeQuery() {
      if (!this.query.trim() || this.loading) return

      this.loading = true
      this.error = null
      this.result = null
      this.page = 1

      try {
        const data = await api.executeSqlQuery(this.connection, this.query)

        if (data.success) {
          this.result = data
          this.saveToHistory(this.connection, this.query)
        } else {
          this.error = data.error || 'Erreur inconnue'
        }
      } catch (err) {
        this.error = err.message || 'Erreur lors de l\'exécution de la requête'
      } finally {
        this.loading = false
      }
    },

    clearQuery() {
      this.query = ''
      this.result = null
      this.error = null
      this.page = 1
    },

    loadFromHistory(item) {
      this.connection = item.connection
      this.query = item.query
      this.showHistory = false
      this.result = null
      this.error = null
    },

    loadQuickQuery(quick) {
      this.connection = quick.connection
      this.query = quick.query
      this.result = null
      this.error = null
    },

    saveToHistory(connection, query) {
      const shortened = query.length > 120 ? query.slice(0, 120) + '…' : query
      const item = {
        connection,
        query: shortened,
        fullQuery: query,
        time: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
      }
      // Dé-dupliquer par requête identique
      this.history = [item, ...this.history.filter(h => h.fullQuery !== query)].slice(0, MAX_HISTORY)
      try {
        localStorage.setItem(HISTORY_KEY, JSON.stringify(this.history))
      } catch (e) {
        // localStorage peut être indisponible
      }
    },

    loadHistory() {
      try {
        const raw = localStorage.getItem(HISTORY_KEY)
        if (raw) this.history = JSON.parse(raw)
      } catch (e) {
        this.history = []
      }
    },

    clearHistory() {
      this.history = []
      localStorage.removeItem(HISTORY_KEY)
    },

    exportCsv() {
      if (!this.result || this.result.rows.length === 0) return

      const escape = (val) => {
        if (val === null || val === undefined) return ''
        const str = String(val)
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`
        }
        return str
      }

      const header = this.result.columns.map(escape).join(',')
      const lines = this.result.rows.map(row =>
        this.result.columns.map(col => escape(row[col])).join(',')
      )
      const csv = [header, ...lines].join('\n')

      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `sql_export_${this.result.connection}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`
      a.click()
      URL.revokeObjectURL(url)
    },
  },
}
</script>

<style scoped>
textarea {
  min-height: 140px;
}
table td {
  max-width: 300px;
}
</style>
